import { FEAT, SKILL_FX, SKILLS, formFx } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, UqDue } from '../types.ts';
import { damageEnemy, enemiesNear, ev, findEnemy, healHero, taunt, targetable, wardHero } from './combat.ts';
import { skillLevelBp } from './create.ts';
import { featPieceAllies, featPieceTest, featUse } from './feats.ts';
import { formFinal, kitSlot } from './forms.ts';
import { bigOne, due, heroById, pieceStun } from './panoply.ts';
import { slowIt } from './skills.ts';
import { stunFor } from './towers.ts';
import { fx, hasUq, nearSegment } from './uniques.ts';

/**
 * The barbarian's panoply in a fight (docs/PANOPLIA.md §10): its nine pieces, called by the skills' programs (cast.ts
 * `special` steps), by a nightmare's death (panoplyDied), by a basic blow (heroHitExtras) and by the queue of what
 * comes later (panoply.ts). What each piece's feats count is counted here too, in one line each.
 */

const live = (s: BattleState, o: Enemy): boolean => o.hp > 0 && targetable(s, o) && o.kind !== 'boneWall';

/** The nearest standing nightmare within `r` of a point that isn't in `skip` (the lowest id on a tie). */
const nearestBut = (s: BattleState, x: number, y: number, r: number, skip: readonly number[]): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of s.enemies) {
    if (!live(s, o) || skip.includes(o.id) || !withinRange(o.x, o.y, x, y, r)) continue;
    const dd = dist2(o.x, o.y, x, y);
    if (dd < bd || (dd === bd && best && o.id < best.id)) {
      bd = dd;
      best = o;
    }
  }
  return best;
};

/** Pushes a nightmare `far` away from a point, as a shove does, even one too big for a shove (the brawler's final form). */
const throwBack = (s: BattleState, e: Enemy, fx0: number, fy0: number, far: number): void => {
  const d = isqrt(dist2(e.x, e.y, fx0, fy0));
  const to = d > 0 ? { x: e.x + idiv((e.x - fx0) * far, d), y: e.y + idiv((e.y - fy0) * far, d) } : { x: e.x + far, y: e.y };
  if (!heroWalkable(s, cellAtWorld(s, to.x, to.y))) return;
  e.x = to.x;
  e.y = to.y;
  e.next = -1;
};

/* ---------- Gran hacha del pastor (Hacha arrojadiza) ---------- */

/** One axe at `e`: struck twice (out and back) by the program, it leaps on its way to the nearest other. Returns its kills. */
const axeLeap = (s: BattleState, h: Hero, e: Enemy, dmg: number, skip: number[]): number => {
  const f = fx('shepherdAxe', h);
  const o = nearestBut(s, e.x, e.y, f.range as number, skip);
  let kills = e.hp <= 0 ? 1 : 0;
  if (!o) return kills;
  skip.push(o.id);
  damageEnemy(s, o, mulBp(dmg, f.damageBp as number), { pid: h.pid });
  if (o.hp <= 0) kills++;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: o.x, ty: o.y, kind: 'axeLeap' });
  featUse(s, h, 'shepherdAxe', 1);
  return kills;
};

/** The shepherd's axe: the thrown axe leaps to another; with Par de hachas (its final form), a second axe at a second target. */
export const axeJump = (s: BattleState, h: Hero, e: Enemy, dmg: number): void => {
  const skip = [e.id];
  let kills = axeLeap(s, h, e, dmg, skip);
  if (formFinal(h, 'shepherdAxe')) {
    const two = nearestBut(s, h.x, h.y, SKILLS.throwingAxe.castRange, skip);
    if (two) {
      skip.push(two.id);
      damageEnemy(s, two, dmg, { pid: h.pid });
      damageEnemy(s, two, dmg, { pid: h.pid });
      ev(s, { t: 'beam', x: h.x, y: h.y, tx: two.x, ty: two.y, kind: 'axe' });
      kills = imax(kills, axeLeap(s, h, two, dmg, skip));
    }
  }
  if (kills >= FEAT.need.axe) featPieceTest(s, h, 'shepherdAxe');
};

/* ---------- Gran hacha del luchador (Patada) ---------- */

/** A kick that felled its nightmare before it got up: the kick back (once every `every` ticks), and the run of them. */
const kickFelled = (s: BattleState, h: Hero): boolean => {
  featUse(s, h, 'brawlerAxe', 1);
  h.kickHit = 1;
  h.kickRun = (h.kickRun ?? 0) + 1;
  if (h.kickRun >= FEAT.need.kicks) {
    featPieceTest(s, h, 'brawlerAxe');
    h.kickRun = 0;
  }
  if (h.kickAt !== undefined && s.tick - h.kickAt < (fx('brawlerAxe', h).every as number)) return false;
  h.kickAt = s.tick;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'kickAgain' });
  return true;
};

/**
 * The brawler's axe, after the kick: what it knocked down carries the barbarian's mark until it gets up; one felled by
 * the kick itself recharges it now (returns true: the cast leaves no cooldown). Patada a los grandes (its final form):
 * an elite, too big for a kick, is thrown `pushBp` of the way.
 */
export const kickMark = (s: BattleState, h: Hero, e: Enemy, push: number): boolean => {
  // A kick whose nightmare got up (or never fell) ends the run.
  if (!h.kickHit) h.kickRun = 0;
  h.kickHit = 0;
  if (e.hp <= 0) return kickFelled(s, h);
  if (formFinal(h, 'brawlerAxe') && e.elite && stunFor(e, 1) === 0 && !bigOne(e)) throwBack(s, e, h.x, h.y, mulBp(push, formFx('brawlerAxe', 'final').pushBp as number));
  if (e.stun > 0) {
    e.kickBy = h.id;
    e.kickUntil = s.tick + e.stun;
  }
  return false;
};

/* ---------- Gran hacha del carretero (Golpe brutal) ---------- */

/**
 * The carter's axe, after the brutal blow (`base`: the blow without its wounds, `d`: as it landed): it mends what the
 * wounds added. Filo del carretero (its final form): below a share of its life, the blow lands around its target too.
 */
export const carterHeal = (s: BattleState, h: Hero, e: Enemy, base: number, d: number): void => {
  const low = h.hp * 10000 < h.stats.maxHp * FEAT.need.brutalLowBp;
  if (low && h.brutalLow === undefined) h.brutalLow = s.tick;
  if (formFinal(h, 'carterAxe')) {
    const ff = formFx('carterAxe', 'final');
    if (h.hp * 10000 < h.stats.maxHp * (ff.belowBp as number)) {
      for (const o of enemiesNear(s, e.x, e.y, ff.radius as number, (x) => x.id !== e.id && live(s, x))) damageEnemy(s, o, d, { pid: h.pid });
      ev(s, { t: 'boom', x: e.x, y: e.y, r: ff.radius as number, kind: 'carterEdge' });
    }
  }
  const healed = healHero(s, h, mulBp(imax(0, d - base), fx('carterAxe', h).healBp as number), h.pid);
  featUse(s, h, 'carterAxe', healed);
  if (h.brutalLow === undefined) return;
  if (s.tick - h.brutalLow > FEAT.need.brutalTicks) h.brutalLow = low ? s.tick : undefined;
  else if (h.hp * 10000 > h.stats.maxHp * FEAT.need.brutalHighBp) {
    featPieceTest(s, h, 'carterAxe');
    h.brutalLow = undefined;
  }
};

/* ---------- Colmillo de toro (Carga) ---------- */

/** The bull's tusk, before the charge lands: the run back goes in the queue (from where it lands to where it left). */
export const chargeBack = (s: BattleState, h: Hero, hits: Enemy[], dmg: number, stun: number, w: number): void => {
  const f = fx('bullTusk', h);
  let n = 0;
  for (const o of hits) if (o.stun > 0) n++;
  featUse(s, h, 'bullTusk', n);
  due(s, { at: s.tick + (f.after as number), k: 'chargeBack', hid: h.id, x: h.x, y: h.y, n: [mulBp(dmg, f.backBp as number), mulBp(stun, f.backBp as number), w, n] });
};

const chargeBackRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, stun = 0, w = 0, went = 0] = d.n ?? [];
  if (!heroWalkable(s, cellAtWorld(s, d.x, d.y))) return;
  const fromX = h.x;
  const fromY = h.y;
  let n = 0;
  for (const o of s.enemies) {
    if (!live(s, o) || !nearSegment(o.x, o.y, fromX, fromY, d.x, d.y, w)) continue;
    damageEnemy(s, o, dmg, { pid: h.pid });
    const st = stunFor(o, stun);
    if (st > o.stun) o.stun = st;
    if (st > 0) n++;
  }
  h.x = d.x;
  h.y = d.y;
  h.destX = d.x;
  h.destY = d.y;
  h.ax = d.x;
  h.ay = d.y;
  h.path = [];
  h.moving = false;
  ev(s, { t: 'beam', x: fromX, y: fromY, tx: d.x, ty: d.y, kind: 'chargeBack' });
  featUse(s, h, 'bullTusk', n);
  if (went + n >= FEAT.need.charge) featPieceTest(s, h, 'bullTusk');
  // Polvareda (its final form): the ground it broke both ways slows what crosses it.
  if (formFinal(h, 'bullTusk')) {
    const ff = formFx('bullTusk', 'final');
    due(s, { at: s.tick + 1, k: 'dust', hid: h.id, x: fromX, y: fromY, n: [d.x, d.y, w, ff.ticks as number] });
  }
};

/** Polvareda: every few ticks while it lasts, what is on the broken path goes slower. */
const DUST_EVERY = 6;
const dustRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const [tx = 0, ty = 0, w = 0, left = 0] = d.n ?? [];
  const ff = formFx('bullTusk', 'final');
  for (const o of s.enemies) if (o.hp > 0 && nearSegment(o.x, o.y, d.x, d.y, tx, ty, w)) slowIt(o, ff.slowBp as number, DUST_EVERY + 1);
  ev(s, { t: 'beam', x: d.x, y: d.y, tx, ty, kind: 'dustTrail' });
  if (left > DUST_EVERY) due(s, { ...d, at: s.tick + DUST_EVERY, n: [tx, ty, w, left - DUST_EVERY] });
};

/* ---------- Cráneo de lobo (Grito intimidante) ---------- */

/** Pánico (the skull's final form): what the fear stunned hits softer a while after it gets up (the big ones, half of it). */
const panicOn = (e: Enemy, stun: number): void => {
  const ff = formFx('wolfSkull', 'final');
  const bp = e.elite || bigOne(e) ? 10000 - idiv(10000 - (ff.bp as number), 2) : (ff.bp as number);
  const t = stun + (ff.ticks as number);
  e.blindBp = (e.blind ?? 0) > 0 ? imin(e.blindBp ?? 10000, bp) : bp;
  if ((e.blind ?? 0) < t) e.blind = t;
};

/** The wolf's skull, after the shout: each one it stunned carries the fear until it gets up. */
export const fearMark = (s: BattleState, h: Hero, hits: Enemy[]): void => {
  h.fearN = 0;
  const fin = formFinal(h, 'wolfSkull');
  for (const o of hits) {
    if (o.hp <= 0 || o.stun <= 0) continue;
    o.fearBy = h.id;
    o.fearUntil = s.tick + o.stun;
    o.fearGen = 0;
    if (fin) panicOn(o, o.stun);
  }
};

/** Generations of the fear's spread (the shout's, then those it spread to, then theirs). */
const FEAR_GENS = 3;

const fearSpread = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('wolfSkull', h);
  const gen = (d.n?.[0] ?? 0) + 1;
  const fin = formFinal(h, 'wolfSkull');
  let n = 0;
  for (const o of enemiesNear(s, d.x, d.y, f.radius as number, (x) => live(s, x))) {
    const st = pieceStun(o, f.stun as number);
    if (st <= o.stun) continue;
    o.stun = st;
    n++;
    if (fin) panicOn(o, st);
    if (gen < FEAR_GENS) {
      o.fearBy = h.id;
      o.fearUntil = s.tick + st;
      o.fearGen = gen;
    }
  }
  if (!n) return;
  ev(s, { t: 'boom', x: d.x, y: d.y, r: f.radius as number, kind: 'fearSpread' });
  featUse(s, h, 'wolfSkull', n);
  const was = h.fearN ?? 0;
  h.fearN = was + n;
  if (was < FEAT.need.fear && h.fearN >= FEAT.need.fear) featPieceTest(s, h, 'wolfSkull');
};

/* ---------- Colmillo de la curtidora (Sangría) ---------- */

/** A stain's pulse: what steps in bleeds as the bloodletting's blows bleed. */
const STAIN_EVERY = 12;

const bleedOn = (h: Hero, e: Enemy): void => {
  const f = SKILL_FX.bloodletting;
  e.dotDmg = mulBp(mulBp(f.damage, skillLevelBp(h.level)), h.stats.powerBp);
  e.dotType = 'physical';
  e.dotEvery = f.every;
  e.dotTicks = f.ticks;
  e.lastPid = h.pid;
  heavyBlood(h, e);
};

/** Sangre que pesa (the fang's final form): what the barbarian bleeds goes slower. */
export const heavyBlood = (h: Hero, e: Enemy): void => {
  if (formFinal(h, 'tannerFang')) slowIt(e, formFx('tannerFang', 'final').slowBp as number, SKILL_FX.bloodletting.ticks);
};

const stainLand = (s: BattleState, h: Hero, e: Enemy): void => {
  const f = fx('tannerFang', h);
  let mine = 0;
  for (const q of s.uqDue ?? []) if (q.k === 'stain' && q.hid === h.id) mine++;
  if (mine >= (f.max as number)) return;
  due(s, { at: s.tick + 1, k: 'stain', hid: h.id, x: e.x, y: e.y, n: [f.ticks as number, 0], ids: [] });
};

const stainRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('tannerFang', h);
  const [left = 0, done = 0] = d.n ?? [];
  const ids = d.ids ?? [];
  let n = 0;
  for (const o of enemiesNear(s, d.x, d.y, f.radius as number, (x) => live(s, x))) {
    bleedOn(h, o);
    n++;
    if (!ids.includes(o.id)) ids.push(o.id);
  }
  ev(s, { t: 'boom', x: d.x, y: d.y, r: f.radius as number, kind: 'bloodStain' });
  featUse(s, h, 'tannerFang', n);
  let tested = done;
  if (!done && ids.length >= FEAT.need.stain) {
    featPieceTest(s, h, 'tannerFang');
    tested = 1;
  }
  if (left > STAIN_EVERY) due(s, { ...d, at: s.tick + STAIN_EVERY, n: [left - STAIN_EVERY, tested], ids });
};

/* ---------- Pelliza del capataz (Rugido del clan) ---------- */

/** The foreman's pelt, after the roar: whose roar each ally carries (for what it spares), the drum, and the echo at its end. */
export const roarEcho = (s: BattleState, h: Hero, roused: Hero[], ticks: number): void => {
  roarMark(h, roused, ticks);
  due(s, { at: s.tick + ticks, k: 'roarEcho', hid: h.id, x: h.x, y: h.y, n: [ticks] });
};

const roarMark = (h: Hero, roused: Hero[], ticks: number): void => {
  const drum = formFinal(h, 'foremanPelt');
  for (const o of roused) {
    o.roarBy = h.id;
    if (drum && (o.drum ?? 0) < ticks) o.drum = ticks;
  }
};

const roarEchoRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const ticks = mulBp(d.n?.[0] ?? 0, fx('foremanPelt', h).echoBp as number);
  const r = SKILLS.clanRoar.radius;
  // From the ally with the most nightmares around (the barbarian itself counts; the lowest id on a tie).
  let from: Hero | null = null;
  let most = -1;
  for (const o of s.heroes) {
    if (o.downTicks > 0) continue;
    const n = enemiesNear(s, o.x, o.y, r, (x) => live(s, x)).length;
    if (n > most) {
      most = n;
      from = o;
    }
  }
  if (!from || ticks <= 0) return;
  const roused = s.heroes.filter((o) => o.downTicks === 0 && withinRange(o.x, o.y, from.x, from.y, r));
  for (const o of roused) {
    if ((o.roar ?? 0) < ticks) o.roar = ticks;
    wardHero(o, SKILL_FX.clanRoar.takenBp, ticks);
  }
  roarMark(h, roused, ticks);
  ev(s, { t: 'boom', x: from.x, y: from.y, r, kind: 'roarEcho' });
  if (roused.filter((o) => o.id !== h.id).length >= 1) featPieceAllies(s, h, 'foremanPelt', roused);
};

/** What a roar's ward spared an ally: the foreman's pelt of the barbarian whose roar it carries counts it. */
export const roarSpared = (s: BattleState, o: Hero, n: number): void => {
  if (o.roarBy === undefined || !((o.roar ?? 0) > 0)) return;
  const h = heroById(s, o.roarBy);
  if (h && hasUq(h, 'foremanPelt')) featUse(s, h, 'foremanPelt', n);
};

/* ---------- Cuero del retador (Duelo) ---------- */

/** The challenger's hide, at the duel's start: its chain begins (no passes, no wins); Arena (its final form) holds the rest off. */
export const duelStart = (h: Hero, e: Enemy): void => {
  e.duelPass = 0;
  e.duelWins = 0;
  if (formFinal(h, 'challengerHide')) h.duelOn = e.id;
};

/** Arena: while its duel lasts, what isn't the challenged one strikes the barbarian at a share. */
export const arenaBp = (s: BattleState, h: Hero, from: Enemy, amount: number): number => {
  const e = h.duelOn !== undefined ? findEnemy(s, h.duelOn) : null;
  if (!e || e.hp <= 0 || e.duelBy !== h.id || !((e.duelTicks ?? 0) > 0)) {
    h.duelOn = undefined;
    return amount;
  }
  return from.id === e.id ? amount : mulBp(amount, formFx('challengerHide', 'final').takenBp as number);
};

const duelWon = (s: BattleState, h: Hero, e: Enemy): void => {
  const wins = (e.duelWins ?? 0) + 1;
  featUse(s, h, 'challengerHide', 1);
  if (wins === FEAT.need.duels) featPieceTest(s, h, 'challengerHide');
  const f = fx('challengerHide', h);
  const passes = e.duelPass ?? 0;
  if (passes >= (f.passes as number)) return;
  due(s, { at: s.tick + 1, k: 'duelPass', hid: h.id, x: e.x, y: e.y, n: [(e.duelTicks ?? 0) + (f.extra as number), passes + 1, wins, e.duelLeechBp ?? 0] });
};

const duelPassRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const [ticks = 0, passes = 0, wins = 0, leech = 0] = d.n ?? [];
  // The biggest within the duel's reach of the barbarian (the lowest id on a tie).
  let e: Enemy | null = null;
  for (const o of enemiesNear(s, h.x, h.y, SKILLS.duel.castRange, (x) => live(s, x))) if (!e || o.maxHp > e.maxHp || (o.maxHp === e.maxHp && o.id < e.id)) e = o;
  if (!e) return;
  e.duelBy = h.id;
  e.duelTicks = ticks;
  e.duelLeechBp = leech || undefined;
  e.duelPass = passes;
  e.duelWins = wins;
  taunt(e, h, ticks);
  h.target = e.id;
  if (formFinal(h, 'challengerHide')) h.duelOn = e.id;
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: e.x, ty: e.y, kind: 'duel' });
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'duel' });
};

/* ---------- Pieles de los ancestros (Furia ancestral) ---------- */

/** The ancestors' pelts, at the fury's start: its count of blows and spirits begins. */
export const furyStart = (h: Hero): void => {
  h.furyHits = 0;
  h.spiritN = 0;
};

/** A blow in the fury: every few, a spirit leaps from its target to another and strikes it with the barbarian's attack. */
const furySpirit = (s: BattleState, h: Hero, e: Enemy): void => {
  const f = fx('ancestorPelts', h);
  h.furyHits = (h.furyHits ?? 0) + 1;
  if (h.furyHits % (f.every as number) !== 0) return;
  const o = nearestBut(s, e.x, e.y, f.range as number, [e.id]);
  if (!o) return;
  damageEnemy(s, o, mulBp(mulBp(h.stats.attack, h.stats.powerBp), f.damageBp as number), { pid: h.pid });
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: o.x, ty: o.y, kind: 'spirit' });
  featUse(s, h, 'ancestorPelts', 1);
  h.spiritN = (h.spiritN ?? 0) + 1;
  if (h.spiritN === FEAT.need.spirits) featPieceTest(s, h, 'ancestorPelts');
  // Espíritus que curan (its final form): a spirit that kills mends the barbarian.
  if (o.hp <= 0 && formFinal(h, 'ancestorPelts')) healHero(s, h, mulBp(h.stats.maxHp, formFx('ancestorPelts', 'final').healBp as number), h.pid);
};

/* ---------- the hooks ---------- */

/** A barbarian's basic blow landed (heroHitExtras): the fury's spirits, the fang's heavy blood. */
export const barbarianHit = (s: BattleState, h: Hero, e: Enemy): void => {
  if ((h.areaBlow ?? 0) > 0 && !h.areaHoly && hasUq(h, 'ancestorPelts')) furySpirit(s, h, e);
  if ((h.bleedOn ?? 0) > 0 && e.hp > 0) heavyBlood(h, e);
};

/** A nightmare died: the kick's, the fear's, the stain's and the duel's pieces. */
export const barbarianDied = (s: BattleState, e: Enemy): void => {
  if (e.kickBy !== undefined && s.tick <= (e.kickUntil ?? 0)) {
    const h = heroById(s, e.kickBy);
    if (h && h.downTicks === 0 && kickFelled(s, h)) {
      const slot = kitSlot(h, 'kick');
      if (slot >= 0) h.skillCd[slot] = 0;
    }
  }
  if (e.fearBy !== undefined && s.tick <= (e.fearUntil ?? 0)) due(s, { at: s.tick + 1, k: 'fearSpread', hid: e.fearBy, x: e.x, y: e.y, n: [e.fearGen ?? 0] });
  if ((e.dotTicks ?? 0) > 0 && e.dotType === 'physical' && e.lastPid) {
    const h = s.heroes.find((o) => o.pid === e.lastPid);
    if (h && hasUq(h, 'tannerFang')) stainLand(s, h, e);
  }
  if (e.duelBy !== undefined && (e.duelTicks ?? 0) > 0) {
    const h = heroById(s, e.duelBy);
    if (h && h.downTicks === 0 && hasUq(h, 'challengerHide')) duelWon(s, h, e);
  }
};

/** What the barbarian's pieces left for this tick (panoply.ts runs the queue). Returns whether it was one of them. */
export const barbarianDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'chargeBack') chargeBackRun(s, h, d);
  else if (d.k === 'dust') dustRun(s, h, d);
  else if (d.k === 'fearSpread') fearSpread(s, h, d);
  else if (d.k === 'stain') stainRun(s, h, d);
  else if (d.k === 'roarEcho') roarEchoRun(s, h, d);
  else if (d.k === 'duelPass') duelPassRun(s, h, d);
  else return false;
  return true;
};
